import React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy';
}

const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, type }) => {
  const isTerms = type === 'terms';
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-none w-screen h-screen m-0 p-0 bg-background flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-800 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {isTerms ? 'Terms & Conditions' : 'Privacy Policy'}
          </h1>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>
        
        <ScrollArea className="flex-1 h-full">
          <div className="px-6 py-8 max-w-4xl mx-auto">
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-lg mb-6 text-gray-700 dark:text-gray-200">
                Effective Date: 05/06/2025
              </p>

              {isTerms ? (
                <>
                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">1. Introduction</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      Welcome to Free Pomodoro Timer ("we", "our", or "us"). These Terms and Conditions ("Terms") govern your access to and use of the Free Pomodoro Timer website and any related services (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      If you do not agree with these Terms, please do not use the Service.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">2. Description of the Service</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      Free Pomodoro Timer is a web-based productivity application designed to help users improve focus and manage time using the Pomodoro Technique. The Service offers timer functionality, task management, prioritization tools, and productivity tracking. Users can optionally create accounts to save data across sessions.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">3. User Accounts</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      You may create an account to access certain features. You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account. You agree to provide accurate and up-to-date information when creating an account.
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We reserve the right to suspend or terminate accounts that violate these Terms or misuse the Service.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">4. User Data</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      You retain ownership of the data you input into the Service, including tasks, labels, settings, and preferences.
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We will not share, sell, or use your personal data for any purpose other than delivering and improving the Service. For more information, refer to our Privacy Policy.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">5. Acceptable Use</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      You agree not to use the Service in a way that:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-200">
                      <li>Violates any local, national, or international law or regulation.</li>
                      <li>Harms or attempts to harm minors.</li>
                      <li>Harasses or causes distress to other users.</li>
                      <li>Gains unauthorized access to or disrupts the Service, servers, or networks.</li>
                      <li>Scrapes, mines, or uses bots to extract data from the Service.</li>
                    </ul>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We reserve the right to monitor and remove any content or user that violates these rules.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">6. Service Availability & Modifications</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We strive to ensure the Service is reliable and available at all times. However, we do not guarantee uninterrupted access. The Service may be updated or modified at any time, and features may be added or removed without notice.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">7. Intellectual Property</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      All content and code on the Service, except for user-generated data and external assets (e.g., Unsplash photos), are the intellectual property of Free Pomodoro Timer. You may not copy, modify, distribute, or create derivative works without explicit permission.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">8. Disclaimer of Warranties</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      The Service is provided "as is" without warranties of any kind. We do not warrant that it will be error-free, uninterrupted, or secure. Use of the Service is at your own risk.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">9. Limitation of Liability</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      To the fullest extent permitted by law, Free Pomodoro Timer shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">10. Changes to These Terms</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We may update these Terms from time to time. Material changes will be posted on the Service or sent to you via email. Continued use of the Service after changes implies acceptance of the new Terms.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">11. Governing Law & Jurisdiction</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      These Terms are governed by the laws of the United Kingdom. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">12. Contact Us</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      If you have questions or concerns about these Terms, please contact us at:
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      Email: [leave blank]
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      Website: [leave blank]
                    </p>
                  </section>
                </>
              ) : (
                <>
                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">1. Introduction</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      Your privacy matters to us. This Privacy Policy explains how Free Pomodoro Timer ("we", "our", "us") collects, uses, and protects your information when you use our website and services (the "Service").
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      By accessing or using the Service, you agree to the terms of this Privacy Policy.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">2. Information We Collect</h2>
                    
                    <h3 className="text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100">2.1 Personal Data</h3>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      When you create an account or interact with the Service, we may collect personal information you provide such as:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-200">
                      <li>Name (if provided)</li>
                      <li>Email address</li>
                      <li>Preferences or settings related to your timer and tasks</li>
                    </ul>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We do not collect sensitive personal data such as addresses, payment details, or government-issued identifiers.
                    </p>

                    <h3 className="text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100">2.2 Usage Data</h3>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We may collect anonymized usage data such as:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-200">
                      <li>Page views</li>
                      <li>Session duration</li>
                      <li>Device type and browser</li>
                      <li>General location (city-level, from IP)</li>
                    </ul>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      This data helps us understand how users interact with the Service and improve the experience.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">3. How We Use Your Data</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We use your data to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-200">
                      <li>Deliver core functionality of the timer and task tools</li>
                      <li>Save your preferences and task history</li>
                      <li>Improve the app through usage insights</li>
                      <li>Communicate product updates (only if you opt in)</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">4. Cookies & Analytics</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We may use minimal, essential cookies to store session data. We do not use tracking cookies or third-party advertising cookies.
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We may use privacy-focused analytics tools to understand how the app is used (e.g., Plausible, Fathom). No personally identifiable data is stored or shared.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">5. Data Retention</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We retain your task and timer data as long as your account remains active. You can delete your account at any time, which will permanently remove your data from our servers.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">6. Data Sharing</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We do not sell, rent, or share your personal data with third parties.
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We may use secure third-party tools to support core functionality (e.g., authentication, hosting), but only those that meet strict data protection standards.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">7. Data Security</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We implement industry-standard measures to protect your data, including encryption, secure backups, and access controls. However, no system is completely secure, and we encourage users to use strong passwords and log out when finished.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">8. Your Rights (GDPR & Global)</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      You have the right to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-200">
                      <li>Access the personal data we hold about you</li>
                      <li>Correct any inaccuracies in your data</li>
                      <li>Delete your data at any time</li>
                      <li>Withdraw consent (where applicable)</li>
                    </ul>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      To request any of these actions, please contact us at the address below.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">9. Changes to This Policy</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      We may update this Privacy Policy as our service evolves. If changes are significant, we will notify users via the app or email. Continued use of the Service means you accept the updated policy.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">10. Contact Us</h2>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      If you have questions or requests regarding your privacy, you can contact us at:
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      Email: [leave blank]
                    </p>
                    <p className="mb-4 text-gray-700 dark:text-gray-200">
                      Website: [leave blank]
                    </p>
                  </section>
                </>
              )}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default LegalModal;
